import React from 'react';
import { i18n } from 'app/utils/i18n';
import { useDraggableList } from 'app/utils/hooks/useDraggableList';
import { ClassDefinition } from './ClassEditor';
import { ClassMap } from '../utils/class-map';
import { ListKey, ListRow, parseEntryLabel } from '../utils/class-lists';
import { isBuiltinSelector } from '../utils/builtin-selectors';

interface ClassListGroupProps {
  list: ListKey | null;
  rows: ListRow[];
  classMap: ClassMap;
  titleKey: string;
  hintKey?: string;
  onReorder: (list: ListKey, order: string[]) => void;
  onRelabel: (list: ListKey, key: string, labelSv: string, labelEn: string) => void;
  onEdit: (className: string) => void;
  onClone: (className: string) => void;
  onRemove: (className: string) => void;
  onMigrate: (className: string) => void;
}

export function ClassListGroup({ list, rows, classMap, titleKey, hintKey, onReorder, onRelabel, onEdit, onClone, onRemove, onMigrate }: ClassListGroupProps) {
  const keys = rows.map((row) => row.key);
  const { reorderedList, draggedItem, draggableAttributes } = useDraggableList(keys, (order: string[]) => list && onReorder(list, order));

  if (rows.length === 0) {
    return null;
  }

  const dragAttributes = (key: string) => {
    const attributes = draggableAttributes(key);
    return {
      ...attributes,
      onDragEnter: (event: React.DragEvent) => {
        if (draggedItem == null) {
          event.preventDefault();
          return;
        }
        attributes.onDragEnter(event);
      },
      onDrop: (event: React.DragEvent) => {
        if (draggedItem == null) {
          event.preventDefault();
          return;
        }
        attributes.onDrop(event);
      },
    };
  };

  const rowByKey = new Map(rows.map((row) => [row.key, row]));
  const ordered: ListRow[] = ((reorderedList as string[] | null) ?? keys).map((key) => rowByKey.get(key)).filter(Boolean) as ListRow[];
  const draggable = list !== null;

  const propertySummary = (def: ClassDefinition) =>
    Object.entries(def.properties)
      .filter(([, value]) => value)
      .map(([prop, value]) => `${prop}: ${value}`)
      .join('; ');

  return (
    <tbody className="form-class-styles__group">
      <tr className="form-class-styles__group-title">
        <td colSpan={6}>
          <div className="fs-7 text-uppercase text-muted">{i18n(titleKey)}</div>
          {hintKey && <div className="fs-7 text-muted fst-italic">{i18n(hintKey)}</div>}
        </td>
      </tr>
      {ordered.map((row) => {
        const def = row.className ? classMap[row.className] : null;
        const labels = parseEntryLabel(row.label);

        return (
          <tr
            key={row.key}
            {...(draggable ? dragAttributes(row.key) : {})}
            className={[
              def ? '' : 'form-class-styles__row--element',
              row.key === draggedItem ? 'opacity-25' : '',
              draggable ? 'form-class-styles__row--draggable' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <td className="text-center">
              {draggable && (
                <span className="draggable-handle text-blue-500" title={i18n('dc.class.drag-to-reorder')}>
                  <i className="fa-solid fa-grip-vertical" />
                </span>
              )}
            </td>
            <td>
              <code style={{ color: '#547ba2' }}>{def || !list ? row.key : `<${row.key}>`}</code>
            </td>
            <td className="fs-7">
              {def ? (
                def.labelSv
              ) : (
                <input
                  type="text"
                  className="form-control form-control-sm fs-7"
                  defaultValue={labels.sv}
                  onBlur={(e) => list && onRelabel(list, row.key, e.target.value, labels.en)}
                />
              )}
            </td>
            <td className="fs-7">
              {def ? (
                def.labelEn
              ) : (
                <input
                  type="text"
                  className="form-control form-control-sm fs-7"
                  defaultValue={labels.en}
                  onBlur={(e) => list && onRelabel(list, row.key, labels.sv, e.target.value)}
                />
              )}
            </td>
            <td className="fs-7 text-muted text-truncate" style={{ maxWidth: 220 }}>
              {def ? propertySummary(def) || '—' : i18n('dc.class.builtin-entry')}
            </td>
            <td className="text-end text-nowrap">
              {def && isBuiltinSelector(row.className as string) && (
                <button
                  className="btn btn-sm btn-outline-warning me-1"
                  style={{ padding: '0.125rem 0.4rem', fontSize: '0.75rem' }}
                  title={i18n('dc.builtin.move-to-slot')}
                  onClick={() => onMigrate(row.className as string)}
                >
                  <i className="fa-regular fa-arrow-right-from-bracket" />
                </button>
              )}
              {def && !isBuiltinSelector(row.className as string) && (
                <button
                  className="btn btn-sm btn-outline-info me-1"
                  style={{ padding: '0.125rem 0.4rem', fontSize: '0.75rem' }}
                  title={i18n('dc.class.clone')}
                  onClick={() => onClone(row.className as string)}
                >
                  <i className="fa-regular fa-clone" />
                </button>
              )}
              {def && (
                <button
                  className="btn btn-sm btn-outline-primary me-1"
                  style={{ padding: '0.125rem 0.4rem', fontSize: '0.75rem' }}
                  title={i18n('dc.class.edit')}
                  onClick={() => onEdit(row.className as string)}
                >
                  <i className="fa-regular fa-pen" />
                </button>
              )}
              {def && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  style={{ padding: '0.125rem 0.4rem', fontSize: '0.75rem' }}
                  title={i18n('dc.class.delete')}
                  onClick={() => onRemove(row.className as string)}
                >
                  <i className="fa-regular fa-trash" />
                </button>
              )}
            </td>
          </tr>
        );
      })}
    </tbody>
  );
}
